import { useState, useEffect } from 'react';
import { getFirestoreInstance, collection, onSnapshot } from '@/lib/firebase';

export interface TypingBot {
  userId: string;
  username: string;
  avatarUrl: string | null;
}

/**
 * Listens to the typing subcollection for a daily room and returns
 * bots that currently have an active (non-expired) typing indicator.
 */
export function useBotTyping(roomId: number | null): TypingBot[] {
  const [typingBots, setTypingBots] = useState<TypingBot[]>([]);

  useEffect(() => {
    if (!roomId) return;

    let unsubscribe: (() => void) | null = null;

    getFirestoreInstance().then(firestore => {
      if (!firestore) return;

      const typingRef = collection(firestore, `dailyRoomChats/${roomId}/typing`);
      unsubscribe = onSnapshot(typingRef, (snapshot) => {
        const now = Date.now();
        const active: TypingBot[] = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          // Filter out expired indicators (server sets expiresAt = delay + buffer)
          const expiresAt = data.expiresAt?.toMillis?.() ?? 0;
          if (expiresAt > now) {
            active.push({
              userId: data.userId,
              username: data.username || 'someone',
              avatarUrl: data.avatarUrl || null,
            });
          }
        });
        setTypingBots(active);
      });
    });

    return () => { unsubscribe?.(); };
  }, [roomId]);

  return typingBots;
}
